import { KeyboardEventKey, uuidv4 } from '@blk/bxf-web-components';
import { RefObject } from 'react';

import { useDispatch } from 'react-redux';
import { activeContentPages } from '../constants';
import { useChatBlkStore, useHandleBxfEvent } from '../hooks';
import { useChatBlkContext } from '../providers';
import { ChatBlkQueryService, ChatBlkTelemetryService } from '../services';
import { ChatBlkThunkDispatch } from '../store/store';
import { sendQuery } from '../store/thunk';
import { QueryTypeEnum, telemetryFunctionMap } from '../types';

const {
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_SUBMIT_BUTTON_CLICKED,
    CHATBLK_MFE_FUNCTION_PROMPT_BOX_TEXT_AREA_CLICKED,
    CHATBLK_MFE_FUNCTION_IMPORTANT_INFO_CLICKED,
    // CHATBLK_MFE_FUNCTION_QUERY_CANCELED,
} = telemetryFunctionMap;

interface UseChatPromptBoxReturn {
    promptBoxRef: RefObject<HTMLBxfChatPromptBoxElement>;
    isGeneratingResponse: boolean;
    handleResponseCancelled: (_e: CustomEvent) => Promise<void>;
    handleResponseSubmitted: (event: CustomEvent) => Promise<void>;
    handleFocusInput: (event: CustomEvent) => void;
    handleImportantInfoClicked: (event: Event) => void;
}

/**
 * Custom hook to manage the chat prompt box functionality.
 *
 * @returns {Object} An object containing the following properties and methods:
 * @returns {boolean} memoizedResponseState - Memoized state indicating if any query is currently loading.
 * @returns {Function} handleResponseCancelled - Handles the event when a chat response is cancelled by the user.
 * @returns {Function} handleResponseSubmitted - Handles the submission of a response from the chat prompt box.
 * @returns {Function} handleFocusInput - Handles the focus event for the input box.
 * @returns {Function} handleImportantInfoClicked - Handles the event when the important information is clicked.
 * @returns {Function} handleValueChanged - Handles the event when the value in the prompt box changes.
 */
const useChatPromptBox = (): UseChatPromptBoxReturn => {
    const chatService = ChatBlkQueryService.getInstance();
    const {
        activeQueryId,
        setPaddingHeight,
        setActiveContent,
        removeQuery,
        queries,
    } = useChatBlkStore();
    const { promptBoxRef } = useChatBlkContext();
    const dispatch = useDispatch<ChatBlkThunkDispatch>();
    /**
     * Handles the event when a chat response is cancelled by
     * the user via the footer of ChatBLK.
     *
     * This function should also perform the following actions:
     * 1. Sets the text area back to the last asked query.
     * 2. Pops the most recent query from the chat exchange query stack.
     * 3. Cancels the current query in the chat service.
     * 4. Reenables the prompt box.
     * 5. Focuses the prompt box area.
     *
     * @returns {Promise<void>} A promise that resolves when the prompt box area is focused.
     */
    const handleResponseCancelled = async () => {
        chatService.cancelQuery();

        setPaddingHeight(0);
        await promptBoxRef.current?.setValue?.(
            `${queries.entries[activeQueryId].query || ''}`
        );
        setTimeout(async () => {
            await promptBoxRef?.current.calculateTextBoxHeight();
            await promptBoxRef.current?.focusArea();
        }, 10);

        removeQuery(activeQueryId);
        // setCurrentQueryId(''); // Reset the current query ID
        // setIsQueryCanceled(true); // Reset the query canceled state
        // setIsProgressiveLoading(false); // Set progressive loading to true
    };

    /**
     * Handles the submission of a response from the chat prompt box.
     *
     * @param event - The custom event triggered when the submit button is clicked.
     * @param event.detail - The detail object of the custom event.
     * @param event.detail.value - The value submitted from the chat prompt box.
     */
    const handleResponseSubmitted = async (event: CustomEvent) => {
        // Reset the prompt box height after submission
        await promptBoxRef.current?.setValue?.('');
        const { value } = event.detail;
        dispatch(
            sendQuery({
                prompt: value,
                telemetryFunction:
                    CHATBLK_MFE_FUNCTION_PROMPT_BOX_SUBMIT_BUTTON_CLICKED,
                queryType: QueryTypeEnum.QUERY,
                chatExchangeId: uuidv4(),
                hashId: uuidv4(),
            })
        );
        setTimeout(async () => {
            await promptBoxRef?.current.calculateTextBoxHeight();
        }, 10);
    };

    /**
     * Handles the focus event for the input box.
     *
     * This function is wrapped with `useHandleBxfEvent` to handle the event
     * when the text area in the prompt box is clicked.
     *
     * @param {CustomEvent} event - The custom event triggered when the input box is focused.
     * @returns {Promise<void>} A promise that resolves when the input box is focused.
     */
    const handleFocusInput: (event: CustomEvent) => Promise<void> =
        useHandleBxfEvent(
            null,
            CHATBLK_MFE_FUNCTION_PROMPT_BOX_TEXT_AREA_CLICKED
        );

    /**
     * Handles the event when the important information button selected
     * Will open on important info slot when user click or presses
     * 'Enter' or 'Space' keys while button is in focus.
     *
     */
    const handleImportantInfoClicked = (event: Event) => {
        if (
            'key' in event &&
            event.key !== KeyboardEventKey.Enter &&
            event.key !== KeyboardEventKey.SpaceBar
        ) {
            return;
        }
        setActiveContent(activeContentPages.IMPORTANT_INFO);
        ChatBlkTelemetryService.track(
            CHATBLK_MFE_FUNCTION_IMPORTANT_INFO_CLICKED
        );
    };

    return {
        promptBoxRef,
        isGeneratingResponse: !!activeQueryId,
        handleResponseCancelled,
        handleResponseSubmitted,
        handleFocusInput,
        handleImportantInfoClicked,
    };
};
export default useChatPromptBox;
